<template>
  <div class="bulk-plan-modal">
    <div class="bulk-plan-modal__selection-bar">
      <span class="bulk-plan-modal__selection-count">{{ selectedCount }} geselecteerd</span>
      <span v-if="selectionSummary.totalDuration" class="bulk-plan-modal__selection-meta">
        · {{ formatMinutes(selectionSummary.totalDuration) }}
      </span>
    </div>

    <div class="bulk-plan-modal__info">
      Stelt automatisch de meest efficiënte route voor op basis van adressen en reistijd.
    </div>
    <div v-if="selectionSummary.missingDurationCount" class="bulk-plan-modal__duration-warning">
      {{ missingDurationWarning }}
    </div>

    <div class="bulk-plan-modal__row">
      <DateInput id="bulk-plan-from" label="Van" v-model="planForm.date_from" />
      <DateInput id="bulk-plan-to" label="Tot" v-model="planForm.date_to" />
    </div>
    <SelectInput
      id="bulk-plan-installer"
      label="Monteur"
      v-model="planForm.assigned_to"
      :options="installerSelectOptions"
    />
    <SelectInput
      id="bulk-plan-helper"
      label="Hulpmonteur (optioneel)"
      v-model="planForm.helper_assigned_to"
      :options="helperSelectOptions"
    />
    <SelectInput
      id="bulk-plan-type"
      label="Planningstype"
      v-model="planForm.planning_type"
      :options="planningTypeOptions"
    />

    <div class="bulk-plan-modal__actions">
      <Btn
        variant="primary"
        text="Voorstel maken"
        icon="calendar"
        :disabled="!planForm.assigned_to"
        @click="submitPlan"
      />
    </div>
  </div>
</template>

<script>
  import { computed, ref, watch } from 'vue';
  import Btn from '@/components/partials/Btn.vue';
  import DateInput from '@/components/inputs/DateInput.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import { getPlanningWorkTime, STORAGE_KEY_BULK_PLAN_FORM } from '@/constants/planningConstants';
  import { useUserStore } from '@/store/userStore';

  export default {
    name: 'PlanningBulkPlanModal',
    components: { Btn, DateInput, SelectInput },
    props: {
      selectedCount: { type: Number, default: 0 },
      selectionSummary: {
        type: Object,
        default: () => ({ totalDuration: 0, tentativeCount: 0 }),
      },
      installerOptions: { type: Array, default: () => [] },
      weekStartDefault: { type: String, default: '' },
      onSubmit: { type: Function, required: true },
    },
    setup(props) {
      const userStore = useUserStore();
      const workTime = computed(() => getPlanningWorkTime(userStore.currentOrganization));

      const planningTypeOptions = [
        { value: 'confirmed', label: 'Bevestigd' },
        { value: 'tentative', label: 'Voorlopig' },
      ];

      const loadPlanForm = () => {
        try {
          const raw = localStorage.getItem(STORAGE_KEY_BULK_PLAN_FORM);
          if (raw) return JSON.parse(raw);
        } catch {
          /* ignore */
        }
        const today = new Date().toISOString().split('T')[0];
        return {
          date_from: today,
          date_to: today,
          assigned_to: '',
          helper_assigned_to: '',
          planning_type: 'confirmed',
        };
      };

      const planForm = ref(loadPlanForm());

      watch(
        () => props.weekStartDefault,
        (val) => {
          if (!val) return;
          planForm.value.date_from = val;
          const end = new Date(`${val}T12:00:00`);
          end.setDate(end.getDate() + 6);
          planForm.value.date_to = end.toISOString().split('T')[0];
        },
        { immediate: true }
      );

      watch(
        planForm,
        (val) => {
          localStorage.setItem(STORAGE_KEY_BULK_PLAN_FORM, JSON.stringify(val));
        },
        { deep: true }
      );

      watch(
        () => props.installerOptions,
        (opts) => {
          if (!planForm.value.assigned_to && opts.length) {
            planForm.value.assigned_to = opts[0].value;
          }
        },
        { immediate: true }
      );

      watch(
        () => planForm.value.assigned_to,
        (primary) => {
          if (primary && planForm.value.helper_assigned_to === primary) {
            planForm.value.helper_assigned_to = '';
          }
        }
      );

      const installerSelectOptions = computed(() => {
        if (props.installerOptions.length) return props.installerOptions;
        return [{ value: '', label: 'Geen monteurs' }];
      });

      const helperSelectOptions = computed(() => {
        const none = { value: '', label: 'Geen hulpmonteur' };
        const primary = planForm.value.assigned_to;
        const options = props.installerOptions.filter((o) => o.value && o.value !== primary);
        return [none, ...options];
      });

      const formatMinutes = (mins) => {
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        if (h && m) return `${h}u ${m}m`;
        if (h) return `${h}u`;
        return `${m}m`;
      };

      const missingDurationWarning = computed(() => {
        const count = props.selectionSummary.missingDurationCount || 0;
        if (!count) return '';
        if (count === 1) {
          return 'Let op: 1 werkbon heeft nog geen duur. Deze wordt gepland met de standaardduur van 2 uur.';
        }
        return `Let op: ${count} werkbonnen hebben nog geen duur. Deze worden gepland met de standaardduur van 2 uur.`;
      });

      const submitPlan = () => {
        if (!planForm.value.assigned_to) return;

        const payload = {
          date_from: planForm.value.date_from,
          date_to: planForm.value.date_to,
          assigned_to: planForm.value.assigned_to,
          technician_ids: [planForm.value.assigned_to],
          optimization_goal: 'minimize_travel_time',
          planning_type: planForm.value.planning_type,
          start_location: { type: 'organization_address' },
          end_location: { type: 'same_as_start' },
          work_time: { ...workTime.value },
        };

        if (planForm.value.helper_assigned_to) {
          payload.helper_technician_id = planForm.value.helper_assigned_to;
        }

        props.onSubmit(payload);
      };

      return {
        planForm,
        planningTypeOptions,
        installerSelectOptions,
        helperSelectOptions,
        missingDurationWarning,
        formatMinutes,
        submitPlan,
      };
    },
  };
</script>

<style scoped lang="scss">
  .bulk-plan-modal {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    :deep(.form-group) {
      margin-bottom: 0;
    }

    &__selection-bar {
      display: flex;
      align-items: center;
      gap: 0.375rem;
      font-size: 0.8125rem;
    }

    &__selection-count {
      font-weight: 600;
      color: var(--slate-800);
    }

    &__selection-meta {
      color: var(--slate-500);
    }

    &__info,
    &__duration-warning {
      padding: 0.5rem 0.625rem;
      border-radius: 0.5rem;
      font-size: 0.75rem;
      line-height: 1.4;
    }

    &__info {
      background: var(--slate-50);
      border: 1px solid var(--slate-200);
      color: var(--slate-600);
    }

    &__duration-warning {
      background: var(--amber-50);
      border: 1px solid var(--amber-200);
      color: var(--amber-800);
      font-weight: 500;
    }

    &__row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 0.75rem;
    }

    &__actions {
      display: flex;
      justify-content: flex-end;
      margin-top: 0.5rem;
      padding-top: 1rem;
      border-top: 1px solid var(--slate-200);
    }
  }
</style>
